import { useState, useEffect } from 'react';
import { ShieldCheck, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';

const STORAGE_KEY = 'dr_cookie_consent_v2';

export const readCookieConsent = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

const persistConsent = (choice) => {
  const record = {
    version: 1,
    decided_at: new Date().toISOString(),
    choice, // 'accept_all' | 'essential_only'
    choices: {
      strictly_necessary: true,
      functional: choice === 'accept_all',
      analytics: choice === 'accept_all',
      marketing: false,
    },
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  window.dispatchEvent(new Event('cookie-consent-updated'));
  return record;
};

/**
 * CookieConsent — persistent bottom banner (GDPR/CCPA).
 * Shows on first visit and can be re-opened via the
 * `open-cookie-preferences` window event (see <CookieSettingsLink />).
 */
export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);

  useEffect(() => {
    if (!readCookieConsent()) setVisible(true);
    const handler = () => setVisible(true);
    window.addEventListener('open-cookie-preferences', handler);
    return () => window.removeEventListener('open-cookie-preferences', handler);
  }, []);

  const acceptAll = () => {
    persistConsent('accept_all');
    setVisible(false);
  };

  const essentialOnly = () => {
    persistConsent('essential_only');
    setVisible(false);
  };

  if (!visible) {
    return <PrivacyPolicyDialog open={privacyOpen} onOpenChange={setPrivacyOpen} />;
  }

  return (
    <>
      <div
        data-testid="cookie-consent-banner"
        role="dialog"
        aria-live="polite"
        aria-label="Cookie consent"
        className="fixed inset-x-0 bottom-0 z-[60] border-t border-slate-200 bg-white shadow-[0_-4px_16px_rgba(15,23,42,0.06)]"
      >
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-4 flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="shrink-0 mt-0.5 p-2 rounded-full bg-emerald-50 border border-emerald-100">
              <ShieldCheck className="w-5 h-5 text-emerald-700" aria-hidden="true" />
            </div>
            <p className="text-sm text-slate-700 leading-relaxed">
              We use <strong>essential cookies</strong> for authentication and security.
              Analytics cookies are optional and help us improve the platform.
              You can change your preferences at any time.{' '}
              <button
                type="button"
                data-testid="cookie-privacy-link"
                onClick={() => setPrivacyOpen(true)}
                className="text-sky-700 underline underline-offset-2 hover:text-sky-900"
              >
                Privacy Policy
              </button>
            </p>
          </div>

          <div className="flex items-center gap-2 shrink-0 md:justify-end">
            <Button
              variant="outline"
              onClick={essentialOnly}
              data-testid="cookie-essential-btn"
              className="border-slate-300 text-slate-800 hover:bg-slate-50"
            >
              Essential Only
            </Button>
            <Button
              onClick={acceptAll}
              data-testid="cookie-accept-btn"
              className="bg-slate-900 hover:bg-slate-800 text-white"
            >
              Accept
            </Button>
          </div>
        </div>
      </div>

      <PrivacyPolicyDialog open={privacyOpen} onOpenChange={setPrivacyOpen} />
    </>
  );
};

/** Small footer link to re-open the cookie preferences banner. */
export const CookieSettingsLink = ({ className = '' }) => (
  <button
    type="button"
    data-testid="open-cookie-preferences"
    onClick={() => window.dispatchEvent(new Event('open-cookie-preferences'))}
    className={`text-xs text-slate-500 hover:text-indigo-700 underline underline-offset-2 ${className}`}
  >
    Cookie preferences
  </button>
);

/** In-app Privacy Policy dialog — self-contained so the login page has
 *  no external dependencies. Content sourced from DataRefinery's GDPR posture. */
const PrivacyPolicyDialog = ({ open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent
      data-testid="privacy-policy-dialog"
      className="max-w-2xl max-h-[85vh] overflow-y-auto"
    >
      <DialogHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-slate-100 rounded-lg">
            <Info className="w-5 h-5 text-slate-700" />
          </div>
          <DialogTitle>DataRefinery Privacy Policy</DialogTitle>
        </div>
        <DialogDescription>
          Last updated: {new Date().getFullYear()}. This is a summary — email
          privacy@datarefinery.ai for the full legal document.
        </DialogDescription>
      </DialogHeader>

      <div className="text-sm text-slate-700 space-y-4 leading-relaxed">
        <section>
          <h3 className="font-semibold text-slate-900 mb-1">What we collect</h3>
          <p>Only what you upload (spend data files) and account details
            (name, email, organization). We do not use tracking pixels,
            third-party analytics, or advertising cookies.</p>
        </section>
        <section>
          <h3 className="font-semibold text-slate-900 mb-1">Why we collect it</h3>
          <p>To provide the DataRefinery service — classify spend, generate
            reports, deliver in-app notifications, and comply with legal
            obligations. Nothing else.</p>
        </section>
        <section>
          <h3 className="font-semibold text-slate-900 mb-1">Cookies we set</h3>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Session token</strong> — kept in browser localStorage,
              expires when you sign out.</li>
            <li>No third-party cookies, no cross-site trackers.</li>
          </ul>
        </section>
        <section>
          <h3 className="font-semibold text-slate-900 mb-1">Your GDPR rights</h3>
          <p>Once signed in, go to <strong>Privacy &amp; Data</strong> in the
            sidebar to:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Export a copy of all your data (Right to Access)</li>
            <li>Request permanent erasure (Right to Erasure)</li>
            <li>Scan your records for PII and redact detected patterns</li>
          </ul>
        </section>
        <section>
          <h3 className="font-semibold text-slate-900 mb-1">Data isolation</h3>
          <p>Every DB query is scoped by tenant. There is no cross-tenant read
            path. Sensitive keys are encrypted at rest with Fernet.</p>
        </section>
        <section>
          <h3 className="font-semibold text-slate-900 mb-1">Retention</h3>
          <p>Your data is retained as long as your account is active. Erasure
            requests are executed immediately and logged in an anonymized
            tombstone (SHA-256) for regulatory audit.</p>
        </section>
        <section>
          <h3 className="font-semibold text-slate-900 mb-1">Contact</h3>
          <p>Questions? Email <a href="mailto:privacy@datarefinery.ai"
            className="text-indigo-700 underline">privacy@datarefinery.ai</a>.</p>
        </section>
      </div>

      <DialogFooter>
        <Button onClick={() => onOpenChange(false)} data-testid="privacy-close-btn">
          Close
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
